import React, { useEffect, useState } from 'react';
import { Gift, PlusCircle, X, Clock, Infinity as InfinityIcon, CalendarPlus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../components/ui/popover';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../context/AuthContext';

// Duration options — matches how admins usually gift trial access.
// `0` = unlimited (backend leaves `expires_at` null).
const DURATION_OPTIONS = [
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
  { value: '0', label: 'Unlimited' },
];

// Only positive durations — extending by "0/Unlimited" makes no sense.
const EXTEND_OPTIONS = DURATION_OPTIONS.filter((o) => o.value !== '0');

function describeError(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  let body = '';
  if (typeof detail === 'string') body = detail;
  else if (Array.isArray(detail)) body = detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  else if (detail && typeof detail === 'object') body = detail.message || detail.error || JSON.stringify(detail);
  else if (e?.message) body = e.message;
  const prefix = status ? `HTTP ${status}` : 'Network error';
  return body ? `${prefix} — ${body}` : prefix;
}

function fmtExpiry(iso) {
  if (!iso) return <span className="inline-flex items-center gap-1"><InfinityIcon className="w-3 h-3" /> unlimited</span>;
  const days = Math.round((new Date(iso) - Date.now()) / 86400000);
  if (days < 0) return 'expired';
  if (days === 0) return 'expires today';
  return `${days}d left`;
}

function statusPill(status) {
  const map = {
    active: 'bg-emerald-100 text-emerald-800',
    expired: 'bg-brand-cream text-brand-green/70',
    canceled: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`text-[11px] px-1.5 py-0.5 rounded ${map[status] || map.expired}`}>
      {status}
    </span>
  );
}

function ExtendControl({ row, onExtend }) {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState('30');
  const [busy, setBusy] = useState(false);

  // Unlimited grants can't be meaningfully extended.
  if (row.status === 'active' && !row.expires_at) return null;

  const apply = async () => {
    setBusy(true);
    try {
      await onExtend(row, choice);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost"
                data-testid={`comp-extend-${row.id}`}
                className="text-brand-green hover:bg-brand-cream">
          <CalendarPlus className="w-3 h-3 mr-1" /> Extend
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 space-y-3" data-testid={`comp-extend-popover-${row.id}`}>
        <div>
          <Label htmlFor={`ext-dur-${row.id}`} className="text-xs">Add duration</Label>
          <Select value={choice} onValueChange={setChoice}>
            <SelectTrigger id={`ext-dur-${row.id}`} data-testid={`comp-extend-duration-${row.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXTEND_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}
                            data-testid={`comp-extend-duration-${row.id}-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-[11px] text-brand-green/60">
          {row.status === 'active'
            ? 'Adds to the current expiry date.'
            : 'Reactivates the grant, starting today.'}
        </p>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={apply} disabled={busy}
                  data-testid={`comp-extend-apply-${row.id}`}
                  className="bg-brand-green text-brand-cream hover:bg-brand-green/90">
            {busy ? 'Applying…' : 'Apply'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function CompAccessAdminCard() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: '', duration: '30' });

  const load = async () => {
    try {
      const { data } = await api.get('/admin/comp-access');
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({ title: 'Failed to load grants', description: describeError(e), variant: 'destructive' });
    }
  };

  useEffect(() => { load(); }, []);

  const grant = async () => {
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        duration_days: Number(form.duration),
      };
      const { data } = await api.post('/admin/comp-access', payload);
      toast({
        title: `Granted comp access to ${data.email}`,
        description: data.expires_at ? `Expires ${data.expires_at.slice(0, 10)}` : 'Unlimited access',
      });
      setForm({ email: '', duration: '30' });
      await load();
    } catch (e) {
      toast({ title: 'Grant failed', description: describeError(e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const revoke = async (row) => {
    if (!window.confirm(`Revoke comp access for ${row.email}?`)) return;
    try {
      await api.delete(`/admin/comp-access/${row.id}`);
      toast({ title: `Revoked ${row.email}` });
      await load();
    } catch (e) {
      toast({ title: 'Revoke failed', description: describeError(e), variant: 'destructive' });
    }
  };

  const extend = async (row, days) => {
    try {
      const { data } = await api.post(
        `/admin/comp-access/${row.id}/extend`,
        { duration_days: Number(days) },
      );
      toast({
        title: `Extended ${data.email}`,
        description: data.expires_at
          ? `New expiry ${data.expires_at.slice(0, 10)}`
          : 'Grant is unlimited — extension is a no-op.',
      });
      await load();
    } catch (e) {
      toast({ title: 'Extend failed', description: describeError(e), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-comp-access">
      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-brand-green">
          <PlusCircle className="w-4 h-4 text-brand-gold" />
          <div className="text-sm font-semibold">Grant trial / comp access</div>
        </div>
        <p className="text-xs text-brand-green/70 -mt-1">
          Gifts full app entitlements to any email — even one that hasn't signed up yet.
          Re-granting the same email extends the existing grant.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-3">
          <div>
            <Label htmlFor="comp-email">User email</Label>
            <Input id="comp-email" data-testid="comp-grant-email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   placeholder="user@example.com" type="email" />
          </div>
          <div>
            <Label htmlFor="comp-duration">Duration</Label>
            <Select value={form.duration}
                    onValueChange={(v) => setForm({ ...form, duration: v })}>
              <SelectTrigger id="comp-duration" data-testid="comp-grant-duration">
                <SelectValue placeholder="Duration" />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`comp-duration-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={grant} disabled={busy || !form.email} data-testid="comp-grant-submit"
                    className="bg-brand-green text-brand-cream hover:bg-brand-green/90">
              {busy ? 'Granting…' : 'Grant'}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3 text-brand-green">
          <Gift className="w-4 h-4 text-brand-gold" />
          <div className="text-sm font-semibold">Comp access grants ({rows.length})</div>
        </div>
        {rows.length === 0 && (
          <div className="text-xs text-brand-green/60" data-testid="comp-empty">
            No comp access grants yet.
          </div>
        )}
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} data-testid={`comp-row-${r.id}`}
                 className="flex items-center justify-between gap-3 border-b border-brand-cream last:border-0 py-3">
              <div className="min-w-0">
                <div className="font-medium text-brand-green truncate flex items-center gap-2">
                  {r.email}
                  {statusPill(r.status)}
                </div>
                <div className="text-[11px] text-brand-green/60 flex items-center gap-1 mt-1">
                  <Clock className="w-3 h-3" /> {fmtExpiry(r.expires_at)}
                  {r.granted_at && (
                    <span className="ml-2 text-brand-green/50">
                      granted {String(r.granted_at).slice(0, 10)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {(r.status === 'active' || r.status === 'expired') && (
                  <ExtendControl row={r} onExtend={extend} />
                )}
                {r.status === 'active' && (
                  <Button size="sm" variant="ghost" onClick={() => revoke(r)}
                          data-testid={`comp-revoke-${r.id}`}
                          className="text-red-700 hover:bg-red-50">
                    <X className="w-3 h-3 mr-1" /> Revoke
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
